import React, { useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import FormContainer from '../../../FormContainer';
import Action from '../../../../Action';
import BillsLists from './BillsLists';
import DeleteButton from '../../../../DeleteButton';
import AchCheckStatus from '../../../../AchCheckStatus';
import CheckInfo from './CheckInfo';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';
import ButtonWithConfirmation from '../../../../ButtonWithConfirmation';
import VoidBillPaymentButton from './VoidBillPaymentButton';
import ReversBillPaymentButton from './ReverseBillPaymentButton';
import SettlementArray from './SettlementAlert';

import useBillPaymentForm from '../../../../../forms/billPayments/billPayment';
import BillPaymentService from '../../../../../services/BillPaymentService';
import BillService from '../../../../../services/BillService';
import { useUiContext } from '../../../../../context/UiContext';
import downloadDocument from '../../../../../helpers/downloadDocument';
import { createAchCheckLabels, isOnlyReferenceEditable } from './helpers';
import { useUiStore } from '../../../../../store';

const tabs = [
  {
    label: 'Bills',
  },
  {
    label: 'Bill Payments',
    activeOn: 'Bill Payment',
  },
];

export default function BillPayment({ standalone, setTitle }) {
  const [billLineItemIds, setBillLineItemIds] = useState([]);
  const [isBillPaymentBeingReversed, setIsBillPaymentBeingReversed] =
    useState(false);

  const { showToast, showDialogModal } = useUiContext();

  const navigate = useNavigate();

  const { billPaymentId, id: loanId } = useParams();

  const {
    data: billPayment,
    refetch: refetchBillPayments,
    isFetching,
  } = useQuery({
    queryKey: ['billPayment', billPaymentId],
    queryFn: () => BillPaymentService.getBillPayment(billPaymentId),
  });

  const setIsFetching = useUiStore((state) => state.setIsFetching);

  useEffect(() => {
    if (billPayment && setTitle) {
      const pixelNumber = billPayment?.pixel_number;
      setTitle(pixelNumber ? `Bill Payment - ${pixelNumber}` : 'Bill Payment');
    }
  }, [setTitle, billPayment]);

  // The line items should be checked by default if they belong to the bill_payment through _bill_line_item._bill_payment_bill_line_items
  useEffect(() => {
    if (billPayment && billPayment._bills && billPayment._bills.length) {
      const checkedLineItemIds = new Set();

      billPayment._bills.forEach((bill) => {
        if (bill._bill_line_items && bill._bill_line_items.length) {
          bill._bill_line_items.forEach((bLItem) => {
            if (
              bLItem._bill_payment_bill_line_items &&
              bLItem._bill_payment_bill_line_items.length &&
              bLItem._bill_payment_bill_line_items.some((it) => {
                return it.bill_payment_id === +billPaymentId;
              })
            ) {
              checkedLineItemIds.add(bLItem.id);
            }
          });
        }

        setBillLineItemIds(Array.from(checkedLineItemIds));
      });
    }
  }, [billPayment, billPaymentId]);

  const hasDocument = !!billPayment?.document_id;
  const hasPdfStatement = !!billPayment?.report_pdf_id;

  const { data: unpaidBills, refetch: refetchUnpaidBills } = useQuery({
    queryKey: [
      'unpaidBills',
      billPayment?._vendor?.id,
      billPayment?._lender?.id,
      billPayment?._borrower?.id,
    ],
    queryFn: () =>
      BillService.getBills({
        status: ['active', 'partial'],
        vendorId: billPayment?._vendor?.id,
        lenderId: billPayment?._lender?.id,
        borrowerId: billPayment?._borrower?.id,
      }),
    retry: false,
    enabled: !!billPayment,
  });

  const refetch = async () => {
    try {
      Promise.all([refetchBillPayments(), refetchUnpaidBills()]);
    } catch (error) {
      console.log(error);
    }
  };

  const isBillPaymentFullyApplied = billPayment?.status === 'applied';
  const isBillPaymentUnapplied = billPayment?.status === 'unapplied';

  let billPaymentForm;

  const { mutate: updateBillPayment, isPending } = useMutation({
    mutationFn: (formValues) =>
      BillPaymentService.updateBillPayment({
        billPaymentId,
        formValues,
      }),
    onSuccess: async () => {
      showToast({
        text: 'Bill Payment updated successfully',
        type: 'success',
      });
      await refetch();
      // billPaymentForm.formik.resetForm();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: applyBillPayment } = useMutation({
    // eslint-disable-next-line no-shadow
    mutationFn: (billLineItemIds) => {
      return BillPaymentService.applyBillPayment({
        billPaymentId,
        billLineItemIds,
      });
    },
    onSuccess: async () => {
      showToast({
        text: 'That bill payment has been applied to the selected bill line items',
        type: 'success',
      });
      setBillLineItemIds([]);
      await refetch();
      billPaymentForm.formik.resetForm();
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: payNow, isPending: isPayNowPending } = useMutation({
    mutationFn: () => {
      setIsFetching(true);
      return BillPaymentService.payNow(billPaymentId);
    },
    onSuccess: async () => {
      showToast({
        text: 'Successfully paid',
        type: 'success',
      });
      setIsFetching(false);
      refetch();
    },
    onError: (error) => {
      console.log(error);
      setIsFetching(false);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: deleteBillPayment, isPending: isBillPaymentBeingDeleted } =
    useMutation({
      // eslint-disable-next-line no-shadow
      mutationFn: () => BillPaymentService.deleteBillPayment(billPaymentId),
      onSuccess: async () => {
        showToast({
          text: 'Bill Payment deleted successfully',
          type: 'success',
        });

        navigate(
          loanId ? `/loans/${loanId}?tab=Bill Payments` : '/bill-payments'
        );
      },
      onError: (error) => {
        console.log(error);
        showToast({
          text: error?.response?.data?.message || 'Something went wrong',
          type: 'error',
        });
      },
    });

  const downloadStatement = () => {
    try {
      downloadDocument(
        billPayment.document_id,
        `${billPayment._document?.private_document?.name}.pdf`
      );
    } catch (error) {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    }
  };

  const downloadPdfStatement = () => {
    try {
      downloadDocument(billPayment.report_pdf_id);
    } catch (error) {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    }
  };

  const billPaymentWithCorrectAmount = billPayment
    ? {
        ...billPayment,
        amount: billPayment?.amount || 0,
      }
    : null;

  billPaymentForm = useBillPaymentForm({
    onSubmit: updateBillPayment,
    values: billPaymentWithCorrectAmount,
    isOnlyReferenceEditable: isOnlyReferenceEditable(billPayment),
  });

  const isPayNowSectionVisible =
    isBillPaymentFullyApplied &&
    !billPayment.billcom_sentpay_id &&
    billPayment.source !== 'Outside' &&
    billPayment.source !== 'Check' &&
    billPayment.paid_from !== 'outside';

  return (
    <div>
      {!standalone && (
        <div className="mb-4 flex justify-between md:items-center flex-col md:flex-row gap-2 h-[37px]">
          <AddOrEditPageTabs tabs={tabs} />
        </div>
      )}
      <div className="relative p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        <SettlementArray billPayment={billPayment} />
        <div className="mb-10">
          <FormContainer
            headerRight={
              !isFetching ? (
                <div className="sm:ml-auto flex flex-col sm:flex-row sm:items-center gap-5">
                  <AchCheckStatus labels={createAchCheckLabels(billPayment)} />
                  {isPayNowSectionVisible && (
                    <Action
                      action={payNow}
                      buttonLabel="Pay Now"
                      disabled={isPayNowPending || isFetching}
                    />
                  )}
                  {hasDocument && (
                    <button
                      onClick={downloadStatement}
                      type="button"
                      className="btn dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                    >
                      CSV Statement
                    </button>
                  )}
                  {hasPdfStatement && (
                    <button
                      onClick={downloadPdfStatement}
                      type="button"
                      className="btn dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                    >
                      PDF Statement
                    </button>
                  )}
                  {isBillPaymentUnapplied ? (
                    <DeleteButton
                      trashCanIcon
                      onClick={() => {
                        showDialogModal({
                          title: 'Delete bill payment',
                          type: 'danger',
                          message:
                            'Are you sure you want to delete this bill payment?',
                          onConfirm: () => deleteBillPayment(),
                          confirmButtonLabel: 'Yes, delete it',
                          cancelButtonLabel: 'No, cancel',
                        });
                      }}
                    />
                  ) : (
                    <ReversBillPaymentButton
                      billPayment={billPayment}
                      refetch={refetch}
                      setIsBillPaymentBeingReversed={
                        setIsBillPaymentBeingReversed
                      }
                    />
                  )}
                  {billPayment?.billcom_sentpay_status === 'Scheduled' && (
                    <div>
                      <ButtonWithConfirmation
                        label="Cancel Bill.com Payment"
                        confirmationMessage="Are you sure you want to cancel this bill payment with Bill.com? The payment cannot be reinitiated with this bill payment. This bill payment will need to be reversed and a new one created to pay the bill(s)."
                        confirmButtonLabel="Yes, cancel it"
                        cancelButtonLabel="No, cancel"
                        successMessage="That bill payment has been canceled with Bill.com"
                        onConfirm={() =>
                          BillPaymentService.cancelBillComPayment(billPaymentId)
                        }
                        refetch={refetch}
                      />
                    </div>
                  )}
                  <VoidBillPaymentButton
                    billPayment={billPayment}
                    refetch={refetch}
                  />
                </div>
              ) : null
            }
            title={standalone ? ' ' : `Bill Payment Details`}
            groupSizeOffset={10}
            isVisible={false}
            form={billPaymentForm}
            noBillComId
            isSubmitting={
              isPending ||
              isBillPaymentBeingDeleted ||
              isBillPaymentBeingReversed
            }
            noSubmit={
              isOnlyReferenceEditable(billPayment) ||
              (billPayment?.check_use_lob && billPayment?.check_id)
            }
          />
        </div>

        {billPayment?.source === 'Check' &&
          (billPayment?.check_use_billcom || billPayment?.check_use_lob) && (
            <div className="mb-10">
              <CheckInfo billPayment={billPayment} />
            </div>
          )}
        {!isBillPaymentFullyApplied && (
          <div className="mb-10">
            <Action
              action={() => applyBillPayment(billLineItemIds)}
              title="Apply Bill Payment"
              buttonLabel="Apply"
              disabled={!billLineItemIds.length}
            />
          </div>
        )}

        <BillsLists
          refetch={refetch}
          billPayment={billPayment}
          billLineItemIds={billLineItemIds}
          setBillLineItemIds={setBillLineItemIds}
          isBillPaymentFullyApplied={isBillPaymentFullyApplied}
          unpaidBills={unpaidBills}
        />
      </div>
    </div>
  );
}
